function round3(value) {
  return Math.round(Number(value) * 1000) / 1000;
}

function pointObject(point) {
  if (Array.isArray(point)) {
    return { x: Number(point[0]) || 0, y: Number(point[1]) || 0 };
  }
  return {
    x: Number(point?.x) || 0,
    y: Number(point?.y) || 0,
  };
}

export const PAN_KEYBOARD_STEP = 60;

export function clampPanPosition(position, metrics) {
  const maxLeft = Math.max(
    0,
    (Number(metrics?.scrollWidth) || 0) - (Number(metrics?.clientWidth) || 0),
  );
  const maxTop = Math.max(
    0,
    (Number(metrics?.scrollHeight) || 0) - (Number(metrics?.clientHeight) || 0),
  );
  return {
    left: Math.min(Math.max(0, Number(position?.left) || 0), maxLeft),
    top: Math.min(Math.max(0, Number(position?.top) || 0), maxTop),
  };
}

export function keyboardPanDelta(key, { shiftKey = false } = {}) {
  const step = PAN_KEYBOARD_STEP * (shiftKey ? 3 : 1);
  switch (key) {
    case 'ArrowLeft':
      return { x: -step, y: 0 };
    case 'ArrowRight':
      return { x: step, y: 0 };
    case 'ArrowUp':
      return { x: 0, y: -step };
    case 'ArrowDown':
      return { x: 0, y: step };
    default:
      return null;
  }
}

export function normalizeBounds(bounds, image) {
  const width = Number(image?.width) || 1;
  const height = Number(image?.height) || 1;
  return {
    x: round3((Number(bounds?.x) || 0) / width),
    y: round3((Number(bounds?.y) || 0) / height),
    width: round3((Number(bounds?.width) || 0) / width),
    height: round3((Number(bounds?.height) || 0) / height),
  };
}

export function regionToImageCrop(region, viewport, image) {
  const scaleX = (Number(image?.width) || 1) / (Number(viewport?.width) || 1);
  const scaleY = (Number(image?.height) || 1) / (Number(viewport?.height) || 1);
  return {
    x: Math.round((Number(region?.x) || 0) * scaleX),
    y: Math.round((Number(region?.y) || 0) * scaleY),
    width: Math.round((Number(region?.width) || 0) * scaleX),
    height: Math.round((Number(region?.height) || 0) * scaleY),
  };
}

export function pointFromEvent(event, element, image) {
  const rect = element.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * (Number(image?.width) || rect.width);
  const y = ((event.clientY - rect.top) / rect.height) * (Number(image?.height) || rect.height);
  return {
    x: Math.max(0, Math.round(x)),
    y: Math.max(0, Math.round(y)),
  };
}

export function boundsFromPoints(start, end) {
  const x = Math.min(start.x, end.x);
  const y = Math.min(start.y, end.y);
  return {
    x,
    y,
    width: Math.max(1, Math.abs(end.x - start.x)),
    height: Math.max(1, Math.abs(end.y - start.y)),
  };
}

export function defaultBoundsForTool(tool, point) {
  switch (tool) {
    case 'highlight':
      return { x: point.x - 90, y: point.y - 28, width: 180, height: 56 };
    case 'text':
      return { x: point.x, y: point.y, width: 220, height: 92 };
    case 'pin':
      return { x: point.x - 18, y: point.y - 18, width: 36, height: 36 };
    case 'pen':
      return { x: point.x - 35, y: point.y - 20, width: 70, height: 40 };
    case 'arrow':
      return { x: point.x - 70, y: point.y - 45, width: 140, height: 90 };
    case 'rectangle':
    default:
      return { x: point.x - 90, y: point.y - 60, width: 180, height: 120 };
  }
}

export function clampBounds(bounds, image) {
  const maxWidth = Number(image?.width) || 1;
  const maxHeight = Number(image?.height) || 1;
  const width = Math.min(Math.max(1, bounds.width), maxWidth);
  const height = Math.min(Math.max(1, bounds.height), maxHeight);
  return {
    x: Math.min(Math.max(0, bounds.x), Math.max(0, maxWidth - width)),
    y: Math.min(Math.max(0, bounds.y), Math.max(0, maxHeight - height)),
    width,
    height,
  };
}

export function normalizePenLocalPoints(points, bounds) {
  const width = Math.max(1, Number(bounds?.width) || 1);
  const height = Math.max(1, Number(bounds?.height) || 1);
  const originX = Number(bounds?.x) || 0;
  const originY = Number(bounds?.y) || 0;
  return (points || []).map((point) => {
    const next = pointObject(point);
    return {
      x: round3((next.x - originX) / width),
      y: round3((next.y - originY) / height),
    };
  });
}

export function denormalizePenLocalPoints(localPoints, bounds) {
  const width = Number(bounds?.width) || 1;
  const height = Number(bounds?.height) || 1;
  const originX = Number(bounds?.x) || 0;
  const originY = Number(bounds?.y) || 0;
  return (localPoints || []).map((point) => ({
    x: Math.round(originX + (Number(point?.x) || 0) * width),
    y: Math.round(originY + (Number(point?.y) || 0) * height),
  }));
}

// Arrowhead sizing shared by the live stage and the SVG export so both render
// identically. The head scales with arrow length (22%) inside hard clamps, and
// the shaft thins for short arrows so the head never dwarfs the line.
export function arrowheadGeometry(start, end, baseStrokeWidth = 5) {
  const length = Math.hypot((end?.x || 0) - (start?.x || 0), (end?.y || 0) - (start?.y || 0));
  const headLength = Math.max(10, Math.min(36, length * 0.22));
  const headWidth = headLength * 0.8;
  const strokeWidth = Math.max(2, Math.min(baseStrokeWidth, headLength / 3));
  return { headLength, headWidth, strokeWidth };
}

export function getArrowRenderPoints(bounds, localPoints = null) {
  if (Array.isArray(localPoints) && localPoints.length >= 2) {
    const [start, end] = denormalizePenLocalPoints(localPoints.slice(0, 2), bounds);
    return [start, end];
  }
  return [
    { x: Number(bounds?.x) || 0, y: Number(bounds?.y) || 0 },
    {
      x: (Number(bounds?.x) || 0) + (Number(bounds?.width) || 0),
      y: (Number(bounds?.y) || 0) + (Number(bounds?.height) || 0),
    },
  ];
}

export function getBoundsCenter(bounds) {
  return {
    x: (Number(bounds?.x) || 0) + (Number(bounds?.width) || 0) / 2,
    y: (Number(bounds?.y) || 0) + (Number(bounds?.height) || 0) / 2,
  };
}

export function rotatePoint(point, center, rotationDeg) {
  const radians = (Number(rotationDeg) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
}

export function getSelectionHandleWorldPosition(bounds, rotationDeg, handle) {
  const center = getBoundsCenter(bounds);
  const width = Number(bounds?.width) || 0;
  const height = Number(bounds?.height) || 0;
  const local = {
    nw: { x: center.x - width / 2, y: center.y - height / 2 },
    n: { x: center.x, y: center.y - height / 2 },
    ne: { x: center.x + width / 2, y: center.y - height / 2 },
    e: { x: center.x + width / 2, y: center.y },
    se: { x: center.x + width / 2, y: center.y + height / 2 },
    s: { x: center.x, y: center.y + height / 2 },
    sw: { x: center.x - width / 2, y: center.y + height / 2 },
    w: { x: center.x - width / 2, y: center.y },
    rotate: { x: center.x, y: center.y - height / 2 - 28 },
  }[handle];
  return rotatePoint(local, center, rotationDeg);
}

const OPPOSITE_HANDLE = {
  nw: 'se',
  n: 's',
  ne: 'sw',
  e: 'w',
  se: 'nw',
  s: 'n',
  sw: 'ne',
  w: 'e',
};

export function resizeBoundsFromHandle(bounds, rotationDeg, handle, pointer, options = {}) {
  const minWidth = Number(options.minWidth) || 24;
  const minHeight = Number(options.minHeight) || 24;
  const anchor = getSelectionHandleWorldPosition(bounds, rotationDeg, OPPOSITE_HANDLE[handle]);
  const radians = (Number(rotationDeg) * Math.PI) / 180;
  const ux = Math.cos(radians);
  const uy = Math.sin(radians);
  const vx = -Math.sin(radians);
  const vy = Math.cos(radians);
  const dx = pointer.x - anchor.x;
  const dy = pointer.y - anchor.y;
  const projX = dx * ux + dy * uy;
  const projY = dx * vx + dy * vy;

  let width = Number(bounds?.width) || 1;
  let height = Number(bounds?.height) || 1;
  let centerX = anchor.x;
  let centerY = anchor.y;

  switch (handle) {
    case 'se':
      width = Math.max(minWidth, projX);
      height = Math.max(minHeight, projY);
      centerX = anchor.x + ux * (width / 2) + vx * (height / 2);
      centerY = anchor.y + uy * (width / 2) + vy * (height / 2);
      break;
    case 'sw':
      width = Math.max(minWidth, -projX);
      height = Math.max(minHeight, projY);
      centerX = anchor.x - ux * (width / 2) + vx * (height / 2);
      centerY = anchor.y - uy * (width / 2) + vy * (height / 2);
      break;
    case 'ne':
      width = Math.max(minWidth, projX);
      height = Math.max(minHeight, -projY);
      centerX = anchor.x + ux * (width / 2) - vx * (height / 2);
      centerY = anchor.y + uy * (width / 2) - vy * (height / 2);
      break;
    case 'nw':
      width = Math.max(minWidth, -projX);
      height = Math.max(minHeight, -projY);
      centerX = anchor.x - ux * (width / 2) - vx * (height / 2);
      centerY = anchor.y - uy * (width / 2) - vy * (height / 2);
      break;
    case 'e':
      width = Math.max(minWidth, projX);
      centerX = anchor.x + ux * (width / 2);
      centerY = anchor.y + uy * (width / 2);
      break;
    case 'w':
      width = Math.max(minWidth, -projX);
      centerX = anchor.x - ux * (width / 2);
      centerY = anchor.y - uy * (width / 2);
      break;
    case 's':
      height = Math.max(minHeight, projY);
      centerX = anchor.x + vx * (height / 2);
      centerY = anchor.y + vy * (height / 2);
      break;
    case 'n':
      height = Math.max(minHeight, -projY);
      centerX = anchor.x - vx * (height / 2);
      centerY = anchor.y - vy * (height / 2);
      break;
    default:
      break;
  }

  return {
    x: Math.round(centerX - width / 2),
    y: Math.round(centerY - height / 2),
    width: Math.round(width),
    height: Math.round(height),
  };
}
